import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { AppProvider, useApp } from "@/lib/app-context";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import SalarySheet from "@/pages/SalarySheet";
import StaffPage from "@/pages/StaffPage";
import UsersPage from "@/pages/UsersPage";
import YearSummary from "@/pages/YearSummary";
import SettlementsPage from "@/pages/SettlementsPage";
import PendingPage from "@/pages/PendingPage";

const AuthPage = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)]">
    <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--ink)] text-white">
      <div className="font-heading text-xl font-bold">Salary Book</div>
      <div>
        <h1 className="font-heading text-5xl font-extrabold leading-tight">Every month's salaries,<br />in one tidy sheet.</h1>
        <p className="mt-4 text-white/70 max-w-md">Staff list saved once. Advance, deduct and payable worked out for you.</p>
      </div>
      <div className="font-mono text-sm text-teal-300">35,000 − 5,000 − 2,000 = 28,000</div>
    </div>
    <div className="flex items-center justify-center p-4">{children}</div>
  </div>
);

function Shell() {
  const { me, error } = useApp();
  if (error) return <div className="p-8 text-red-600" data-testid="app-error">{error}</div>;
  if (!me) return <div className="p-8 text-slate-500" data-testid="app-loading">Loading…</div>;
  if (me.role === "pending") return <PendingPage />;
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Header />
      <main className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-6">
        <Routes>
          <Route path="/" element={<SalarySheet />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/staff/:id" element={<YearSummary />} />
          <Route path="/settlements" element={<SettlementsPage />} />
          <Route path="/users" element={me.role === "admin" ? <UsersPage /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const Protected = () => (
  <>
    <Show when="signed-in"><AppProvider><Shell /></AppProvider></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/*" element={<Protected />} />
        </Routes>
        <Toaster richColors position="top-center" />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
